import React, { FC } from "react";
import Box from "@mui/material/Box";
import { Button } from "@mui/material";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { lighten, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { colors } from "../../theme";
import { PricingFooter } from "./PricingFooter";
import CloseIcon from "@mui/icons-material/Cancel";
import { useTranslation } from "react-i18next";

const buttonBarHeight = "60px";

export type IShapeChooserTranslations = {
  title: string;
  cancel: string;
  continue: string;

  shapes: Record<string, string>;
};

export type IShape = {
  name: string;
  description?: string;
  shape: string;
  imageSrc: string;
};

export type ShapeChooserProps = {
  shapes: IShape[];
  shape: string;

  onChangeConfirmed: (shape: string) => void;
  onCancel: () => void;

  translations: IShapeChooserTranslations;
};

export const ShapeChooser: FC<ShapeChooserProps> = (props) => {
  const theme = useTheme();
  const [selectedShape, setSelectedShape] = useState(props.shape);

  return (
    <>
      <Box
        sx={{
          overflow: "auto",
          position: "sticky",
          bottom: 0,
          height: `calc(100vh - ${buttonBarHeight})`,

          [theme.breakpoints.up("md")]: {
            maxHeight: 600,
          },
        }}
      >
        <Box>
          <IconButton
            title={props.translations.cancel}
            sx={{
              position: "absolute",
              top: 0,
              right: 0.5,
              color: theme.colors.grey,

              display: { md: "none" },
            }}
            onClick={() => props.onCancel()}
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ textAlign: "center", marginTop: 2 }} variant="h3">
            {props.translations.title}
          </Typography>
          <Box display="flex" justifyContent="center">
            <List sx={{ margin: 2 }}>
              {props.shapes.map(({ name, shape, description, imageSrc }) => {
                return (
                  <ListItem
                    key={shape}
                    sx={{
                      borderWidth: 2,
                      borderStyle: "solid",
                      backgroundColor:
                        shape === selectedShape
                          ? lighten(colors.coolBlueLight, 0.25)
                          : "#fff",
                      borderColor: "#fff",
                      borderRadius: "5px",
                      marginLeft: 0,
                      marginBottom: 1,
                      paddingTop: 3,
                      paddingBottom: 3,
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: colors.coolBlueLight,
                      },
                    }}
                    onClick={() => {
                      setSelectedShape(shape);
                    }}
                  >
                    <Grid container>
                      <Grid item xs={3}>
                        <img
                          title={name}
                          alt={`${name} icon`}
                          width="100%"
                          src={imageSrc}
                        />
                      </Grid>
                      <Grid item xs={9}>
                        <Box sx={{ marginLeft: 4 }}>
                          <Typography
                            component="div"
                            variant="h4"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {props.translations.shapes[shape]}
                          </Typography>
                          {description ? (
                            <Typography variant="body2" sx={{ marginTop: 1 }}>
                              {description}
                            </Typography>
                          ) : null}
                        </Box>
                      </Grid>
                    </Grid>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Box>
      </Box>
      <PricingFooter sx={{ height: buttonBarHeight, paddingTop: 0.5 }}>
        <Grid container>
          <Grid item xs={6} sx={{ padding: 1 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => props.onCancel()}
            >
              {props.translations.cancel}
            </Button>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{ padding: 1 }}
            onClick={() => props.onChangeConfirmed(selectedShape)}
          >
            <Button variant="contained" color="primary" fullWidth>
              {props.translations.continue}
            </Button>
          </Grid>
        </Grid>
      </PricingFooter>
    </>
  );
};
